/**
 * Ledger journal endpoints (bos-api).
 *
 * Paths are relative to the axios client's base, which already ends in
 * `/api/v1` — so `/ledger/journal-entries` reaches
 * `…/api/v1/ledger/journal-entries`. This mirrors `AccountsEndpoints` and
 * `SettingsEndpoints`, the other live ledger resources.
 *
 * Two shapes here differ from what this client originally assumed, and both are
 * load-bearing:
 *
 * - **Create is a POST on the collection**, not a PUT upsert on the id. The
 *   client-generated id travels in the body, and that is what makes a retried
 *   create idempotent (200 instead of 201, no duplicate).
 * - **The date filter is `DateFrom`/`DateTo`**, not `fromDate`/`toDate`. The
 *   server ignores unknown query parameters silently, so the original names
 *   produced an unfiltered list that looked filtered — the worst failure mode
 *   available to a ledger filter. Everything else binds case-insensitively
 *   (`page`, `pageSize`, `status`, `accountId` all work either way); only these
 *   two are genuinely different names.
 */

/** Ids are server-shaped, but escaping keeps the path well-formed. */
const seg = (value: string): string => encodeURIComponent(value);

export type JournalListParams = {
    page: number;
    pageSize: number;
    /** Concrete status, or omitted for all. */
    status?: string;
    /** Concrete `EntrySource`, or omitted for all. */
    source?: string;
    /** A family of origins — today only `SYSTEM` (bos-api BOS-136). */
    sourceGroup?: string;
    fromDate?: string;
    toDate?: string;
    accountId?: string;
};

const query = ({
    page,
    pageSize,
    status,
    source,
    sourceGroup,
    fromDate,
    toDate,
    accountId,
}: JournalListParams): string => {
    const params = new URLSearchParams();
    params.set('Page', String(page));
    params.set('PageSize', String(pageSize));
    if (status) {
        params.set('Status', status);
    }
    if (source) {
        params.set('Source', source);
    }
    if (sourceGroup) {
        params.set('SourceGroup', sourceGroup);
    }
    if (fromDate) {
        params.set('DateFrom', fromDate);
    }
    if (toDate) {
        params.set('DateTo', toDate);
    }
    if (accountId) {
        params.set('AccountId', accountId);
    }
    return `?${params.toString()}`;
};

export const JournalEndpoints = {
    /** GET — a page of entry summaries, filtered. */
    list: (params: JournalListParams) =>
        `/ledger/journal-entries${query(params)}`,

    /** POST — create. Idempotent on the client-supplied id; 200 or 201. */
    create: () => '/ledger/journal-entries',

    /** PUT — update a draft. Requires the last-seen etag; 409 `STALE_UPDATE`. */
    update: (id: string) => `/ledger/journal-entries/${seg(id)}`,

    /** POST — post the entry. 400 with a single `ApiError` on refusal. */
    post: (id: string) => `/ledger/journal-entries/${seg(id)}/post`,

    /** POST — reverse a posted entry. Optional `{ date, description }` body. */
    reverse: (id: string) => `/ledger/journal-entries/${seg(id)}/reverse`,

    /** GET — one entry, with its lines and validation state. */
    get: (id: string, locale?: string) =>
        `/ledger/journal-entries/${seg(id)}${locale ? `?locale=${seg(locale)}` : ''}`,

    /**
     * DELETE — discard a draft. Posted entries are immutable and the server
     * refuses; the UI only offers this on a DRAFT.
     */
    remove: (id: string) => `/ledger/journal-entries/${seg(id)}`,
};
